import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/utils/api';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import Layout from '@/components/Layout';

const statusColors = {
  occupied: 'bg-green-100 text-green-800',
  vacant: 'bg-yellow-100 text-yellow-800',
  maintenance: 'bg-red-100 text-red-800'
};

export default function Units() {
  const { t } = useTranslation();
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [filterPropertyId, setFilterPropertyId] = useState('all');
  const [formData, setFormData] = useState({
    unit_number: '', property_id: '', unit_type: '1BR', size_sqm: '',
    monthly_rent: '', status: 'vacant', floor: '', bedrooms: '', bathrooms: ''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [unitsRes, propsRes] = await Promise.all([api.getUnits(), api.getProperties()]);
      setUnits(unitsRes.data);
      setProperties(propsRes.data);
    } catch (error) { console.error('Failed to fetch:', error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      size_sqm: formData.size_sqm ? parseFloat(formData.size_sqm) : null,
      monthly_rent: parseFloat(formData.monthly_rent),
      floor: formData.floor ? parseInt(formData.floor) : null,
      bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
      bathrooms: formData.bathrooms ? parseFloat(formData.bathrooms) : null
    };
    try {
      if (editingUnit) { await api.updateUnit(editingUnit.id, data); }
      else { await api.createUnit(data); }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) { console.error('Failed to save:', error); }
  };

  const resetForm = () => {
    setFormData({ unit_number: '', property_id: '', unit_type: '1BR', size_sqm: '', monthly_rent: '', status: 'vacant', floor: '', bedrooms: '', bathrooms: '' });
    setEditingUnit(null);
  };

  const handleEdit = (unit) => {
    setEditingUnit(unit);
    setFormData({
      unit_number: unit.unit_number, property_id: unit.property_id, unit_type: unit.unit_type,
      size_sqm: unit.size_sqm || '', monthly_rent: unit.monthly_rent, status: unit.status,
      floor: unit.floor || '', bedrooms: unit.bedrooms || '', bathrooms: unit.bathrooms || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      try { await api.deleteUnit(id); fetchData(); }
      catch (error) { console.error('Failed to delete:', error); }
    }
  };

  const getPropertyName = (id) => properties.find(p => p.id === id)?.name || '-';

  const filteredUnits = filterPropertyId === 'all' ? units : units.filter(u => u.property_id === filterPropertyId);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">{t('units')}</h1>
          <div className="flex gap-3">
            <Select value={filterPropertyId} onValueChange={setFilterPropertyId}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All properties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetForm}><Plus className="mr-2 h-4 w-4" /> {t('addUnit')}</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingUnit ? t('edit') : t('add')} {t('units')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t('unitNumber')}*</Label>
                      <Input value={formData.unit_number} onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })} required />
                    </div>
                    <div>
                      <Label>{t('properties')}*</Label>
                      <Select value={formData.property_id} onValueChange={(v) => setFormData({ ...formData, property_id: v })} required>
                        <SelectTrigger><SelectValue placeholder="Select property" /></SelectTrigger>
                        <SelectContent>
                          {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t('unitType')}*</Label>
                      <Select value={formData.unit_type} onValueChange={(v) => setFormData({ ...formData, unit_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['Studio', '1BR', '2BR', '3BR', '4BR', 'Villa', 'Shop', 'Office'].map(type => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t('monthlyRent')} (SAR)*</Label>
                      <Input type="number" value={formData.monthly_rent} onChange={(e) => setFormData({ ...formData, monthly_rent: e.target.value })} required />
                    </div>
                    <div>
                      <Label>{t('size')}</Label>
                      <Input type="number" value={formData.size_sqm} onChange={(e) => setFormData({ ...formData, size_sqm: e.target.value })} />
                    </div>
                    <div>
                      <Label>{t('floor')}</Label>
                      <Input type="number" value={formData.floor} onChange={(e) => setFormData({ ...formData, floor: e.target.value })} />
                    </div>
                    <div>
                      <Label>{t('bedrooms')}</Label>
                      <Input type="number" value={formData.bedrooms} onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })} />
                    </div>
                    <div>
                      <Label>{t('bathrooms')}</Label>
                      <Input type="number" value={formData.bathrooms} onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })} />
                    </div>
                    <div>
                      <Label>{t('status')}</Label>
                      <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="vacant">{t('vacant')}</SelectItem>
                          <SelectItem value="occupied">{t('occupied')}</SelectItem>
                          <SelectItem value="maintenance">{t('maintenance')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button type="submit" className="w-full">{t('save')}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{units.length}</div><div className="text-sm text-gray-500">{t('totalUnits')}</div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-green-600">{units.filter(u => u.status === 'occupied').length}</div><div className="text-sm text-gray-500">{t('occupied')}</div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-yellow-600">{units.filter(u => u.status === 'vacant').length}</div><div className="text-sm text-gray-500">{t('vacant')}</div></CardContent></Card>
        </div>

        <Card>
          <CardContent className="pt-6">
            {loading ? <div className="text-center py-8">Loading...</div> : filteredUnits.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No units found.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('unitNumber')}</TableHead>
                    <TableHead>{t('properties')}</TableHead>
                    <TableHead>{t('unitType')}</TableHead>
                    <TableHead>{t('size')}</TableHead>
                    <TableHead>{t('monthlyRent')}</TableHead>
                    <TableHead>{t('status')}</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUnits.map((unit) => (
                    <TableRow key={unit.id}>
                      <TableCell className="font-medium">{unit.unit_number}</TableCell>
                      <TableCell>{getPropertyName(unit.property_id)}</TableCell>
                      <TableCell>{unit.unit_type}</TableCell>
                      <TableCell>{unit.size_sqm ? `${unit.size_sqm} sqm` : '-'}</TableCell>
                      <TableCell>{unit.monthly_rent} SAR</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[unit.status]}`}>
                          {t(unit.status)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(unit)}><Edit2 className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(unit.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
